import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { cn } from '@/lib/utils'
import { easeOutExpo } from '@/lib/motion'
import { useIntro } from '@/components/generals/intro'

type RevealFrom = 'bottom' | 'top' | 'left' | 'right' | 'center'

interface ClipRevealProps {
    children: ReactNode
    className?: string
    delay?: number
    from?: RevealFrom
    zoom?: boolean
}

const HIDDEN: Record<RevealFrom, string> = {
    bottom: 'inset(100% 0% 0% 0%)',
    top: 'inset(0% 0% 100% 0%)',
    left: 'inset(0% 100% 0% 0%)',
    right: 'inset(0% 0% 0% 100%)',
    center: 'inset(48% 48% 48% 48%)'
}

export const ClipReveal: FC<ClipRevealProps> = ({
    children,
    className,
    delay = 0,
    from = 'bottom',
    zoom = true
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const { done } = useIntro()
    const seen = useInView(ref, { once: true, margin: '0px 0px -12% 0px' })
    const inView = seen && done

    return (
        <motion.div
            ref={ref}
            className={cn('overflow-hidden', className)}
            initial={{ clipPath: HIDDEN[from] }}
            animate={inView ? { clipPath: 'inset(0% 0% 0% 0%)' } : undefined}
            transition={{ duration: 1.25, ease: easeOutExpo, delay }}
        >
            <motion.div
                className='h-full w-full'
                initial={zoom ? { scale: 1.2 } : false}
                animate={inView && zoom ? { scale: 1 } : undefined}
                transition={{ duration: 1.7, ease: easeOutExpo, delay }}
            >
                {children}
            </motion.div>
        </motion.div>
    )
}
